import { arrayMove } from '@dnd-kit/sortable';

/** One Handwerker in the order being edited. `technicianId` is a `User.id`. */
export interface OrderItem {
  technicianId: number;
  hidden: boolean;
}

export interface CalendarOrdered<R> {
  row: R;
  /** Hidden by the Leitung — drawn greyed out. Same shape `orderByTrade` returns. */
  muted: boolean;
}

type OrderRow = { technicianId: number; calendarHidden: boolean };

/**
 * The default calendar's order of the Handwerker — the Leitung's choice, saved on the server.
 *
 * Deliberately not `orderByTrade`: that one answers "who fits the Auftrag being planned" and only
 * runs while planning; this one answers "how does the Leitung want the calendar to read" and only
 * runs outside it. The two never see each other's input.
 */
export function orderByCalendar<R extends OrderRow>(
  rows: R[],
  draft: OrderItem[] | null,
): CalendarOrdered<R>[] {
  // Without a draft the server's order is the order — it already has hidden Handwerker last.
  if (!draft) return rows.map((row) => ({ row, muted: row.calendarHidden }));

  const byId = new Map(rows.map((row) => [row.technicianId, row]));
  const drafted = new Set(draft.map((item) => item.technicianId));

  const placed = draft.flatMap((item) => {
    const row = byId.get(item.technicianId);
    return row ? [{ row, muted: item.hidden }] : [];
  });

  // A Handwerker who appeared after the editor opened (another week, a new profile): behind the rest.
  const unplaced = rows
    .filter((row) => !drafted.has(row.technicianId))
    .map((row) => ({ row, muted: row.calendarHidden }));

  return [...placed, ...unplaced];
}

export function toOrderItems(rows: OrderRow[]): OrderItem[] {
  return normalizeOrder(
    rows.map((row) => ({ technicianId: row.technicianId, hidden: row.calendarHidden })),
  );
}

/** Visible Handwerker first, hidden ones after; inside each group the order is kept. */
export function normalizeOrder(items: OrderItem[]): OrderItem[] {
  return [...items.filter((item) => !item.hidden), ...items.filter((item) => item.hidden)];
}

/**
 * Moves one Handwerker to where another one is. A drop on the far side of the visible/hidden
 * boundary is pulled back into the group the Handwerker belongs to — only the eye button changes
 * which group that is.
 */
export function moveItem(items: OrderItem[], activeId: number, overId: number): OrderItem[] {
  const from = items.findIndex((item) => item.technicianId === activeId);
  const to = items.findIndex((item) => item.technicianId === overId);
  if (from < 0 || to < 0 || from === to) return items;

  return normalizeOrder(arrayMove(items, from, to));
}

/** Hiding sends the Handwerker to the very end; showing again puts them last among the visible. */
export function toggleHidden(items: OrderItem[], technicianId: number): OrderItem[] {
  const target = items.find((item) => item.technicianId === technicianId);
  if (!target) return items;

  const rest = items.filter((item) => item.technicianId !== technicianId);
  return normalizeOrder([...rest, { ...target, hidden: !target.hidden }]);
}
